import BaseApiClient from '../BaseApiClient';
import { ListDataParams, ListDataResponse } from '@api-client/types';

/**
 * The sub-product tax configuration endpoints live on the Flightdeck portal API version, so every
 * request here has to carry the Version header.
 */
const API_VERSION = { Version: '2025-11-14' };

export interface SubProductLegalEntityInterface {
  id: number;
  name: string;
  uuid: string;
  order: number;
}

export interface SubProductInterface {
  id: number;
  uuid: string;
  name: string;
  business_unit: {
    id: number;
    name: string;
  } | null;
  /** One of the three tax codes, or null when no treatment has been configured yet. */
  tax_code: string | null;
  tax_code_label: string | null;
  legal_entities: SubProductLegalEntityInterface[];
  default_legal_entity: SubProductLegalEntityInterface | null;
}

export interface TaxCodeOptionInterface {
  code: string;
  label: string;
}

/**
 * The API replaces a sub-product's whole configuration, so every field must be sent. Null and an
 * empty list are how the screen clears one.
 */
export interface SubProductUpdatePayload {
  tax_code: string | null;
  legal_entity_ids: number[];
  default_legal_entity_id: number | null;
}

export class ApiSubProducts extends BaseApiClient {
  async list(
    listData: ListDataParams,
    searchTerm?: string
  ): Promise<ListDataResponse<SubProductInterface>> {
    return new Promise((resolve, reject) => {
      if (searchTerm) {
        if (!listData.filters) listData.filters = {};
        listData.filters.name = searchTerm;
      }

      this.client
        .getList('/sub-products', listData, { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve({
              items: res.data.data,
              pagination: {
                page: res.data.meta.current_page,
                perPage: res.data.meta.per_page,
                total: res.data.meta.total
              }
            });
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  async update(
    id: number,
    payload: SubProductUpdatePayload
  ): Promise<SubProductInterface> {
    return new Promise((resolve, reject) => {
      this.client
        .put(`/sub-products/${id}`, payload, { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }

  /**
   * The three treatments and their labels come from the API so the codes are defined in one place
   * rather than restated here.
   */
  async taxCodes(): Promise<TaxCodeOptionInterface[]> {
    return new Promise((resolve, reject) => {
      this.client
        .get('/tax-codes', { headers: API_VERSION })
        .then((res) => {
          if (res.data) {
            resolve(res.data.data);
          } else {
            reject('Invalid response');
          }
        })
        .catch((err) => reject(err));
    });
  }
}
